import { useOptionalMeasurements } from '../context/MeasurementContext'
import { useState, useRef, useEffect } from 'react'
import { Product } from '../services/product.service'
import { exportProductsToJson, exportProductsToCsv, exportProductsToPdf } from '../utils/productExport'
import {
    ArrowDownTrayIcon,
    DocumentTextIcon,
    TableCellsIcon,
    CodeBracketIcon,
    ChevronDownIcon,
    XMarkIcon,
    CheckCircleIcon,
    AdjustmentsHorizontalIcon,
    ClipboardDocumentIcon
} from '@heroicons/react/24/outline'

interface ProductExportButtonProps {
    products: Product[]
    allProducts: Product[]
    searchQuery?: string
    filterStatus?: string
    selectedAttributes?: string[]
    onExportSuccess?: (message: string) => void
}

export default function ProductExportButton({
    products,
    allProducts,
    searchQuery = '',
    filterStatus = 'all',
    selectedAttributes = [],
    onExportSuccess
}: ProductExportButtonProps) {
    const system = useOptionalMeasurements()?.system ?? 'metric'
    const [isOpen, setIsOpen] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'csv' | 'json'>('pdf')
    const [exportScope, setExportScope] = useState<'filtered' | 'all'>('filtered')
    const [copied, setCopied] = useState(false)

    const dropdownRef = useRef<HTMLDivElement>(null)

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const productsToExport = exportScope === 'filtered' ? products : allProducts

    const getFilterSummary = (): string | undefined => {
        const parts: string[] = []
        if (searchQuery) parts.push(`Recherche "${searchQuery}"`)
        if (filterStatus && filterStatus !== 'all') parts.push(`Statut ${filterStatus}`)
        if (selectedAttributes.length > 0) parts.push(`Contraintes [${selectedAttributes.join(', ')}]`)
        return parts.length > 0 ? parts.join(' | ') : undefined
    }

    const handleQuickExport = (format: 'pdf' | 'csv' | 'json') => {
        setIsOpen(false)
        runExport(format, products)
    }

    const runExport = (format: 'pdf' | 'csv' | 'json', targetProducts: Product[]) => {
        const filterNote = targetProducts === products ? getFilterSummary() : undefined
        const count = targetProducts.length

        if (format === 'json') {
            exportProductsToJson(targetProducts)
            if (onExportSuccess) onExportSuccess(`Export JSON de ${count} produit(s) réussi.`)
        } else if (format === 'csv') {
            exportProductsToCsv(targetProducts, undefined, system)
            if (onExportSuccess) onExportSuccess(`Export CSV de ${count} produit(s) réussi.`)
        } else if (format === 'pdf') {
            exportProductsToPdf(targetProducts, undefined, filterNote, system)
            if (onExportSuccess) onExportSuccess(`Export PDF de ${count} produit(s) réussi.`)
        }
    }

    const handleModalExport = () => {
        runExport(selectedFormat, productsToExport)
        setIsModalOpen(false)
    }

    const handleCopyPreview = () => {
        let content = ''
        if (selectedFormat === 'json') {
            content = JSON.stringify(productsToExport.slice(0, 10), null, 2)
        } else if (selectedFormat === 'csv') {
            const sampleHeaders = ['ID', 'SKU', 'Nom', 'Longueur', 'Largeur', 'Hauteur', 'Poids', 'Statut']
            const sampleRows = productsToExport.slice(0, 5).map(p => [
                p.id || '', p.sku || '', p.name || '', p.length || '', p.width || '', p.height || '', p.weight || '', p.status || 'active'
            ].join(';'))
            content = [sampleHeaders.join(';'), ...sampleRows].join('\n')
        } else {
            content = `Catalogue Produits TMS - ${productsToExport.length} référence(s)\nFormat : PDF\nGénéré le : ${new Date().toLocaleString()}`
        }

        navigator.clipboard.writeText(content)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className="relative inline-block text-left" ref={dropdownRef}>
            {/* Primary Modern Export Button */}
            <div className="inline-flex rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-orange-300 dark:hover:border-orange-600 transition-all duration-200 overflow-hidden">
                <button
                    type="button"
                    onClick={() => handleQuickExport('pdf')}
                    title={`Exporter ${products.length} produit(s) au format PDF`}
                    className="inline-flex items-center gap-2 px-3.5 py-2.5 text-xs font-extrabold uppercase tracking-wider text-slate-800 dark:text-slate-100 hover:bg-orange-50 dark:hover:bg-orange-950/40 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
                >
                    <ArrowDownTrayIcon className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    <span>Exporter</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-black bg-orange-100 dark:bg-orange-950/80 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                        {products.length}
                    </span>
                </button>

                <button
                    type="button"
                    onClick={() => setIsOpen(prev => !prev)}
                    className="px-2 py-2.5 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white border-l border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    aria-label="Options d'export"
                >
                    <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl z-50 overflow-hidden animate-fade-in">
                    <div className="px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-400">
                            Format d&apos;exportation
                        </span>
                        <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400 font-bold">
                            {products.length} réf.
                        </span>
                    </div>

                    <div className="p-1.5 space-y-1">
                        {/* PDF Option */}
                        <button
                            type="button"
                            onClick={() => handleQuickExport('pdf')}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors group"
                        >
                            <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                                <DocumentTextIcon className="w-4.5 h-4.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400">
                                        Document PDF
                                    </span>
                                    <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 font-mono">
                                        .PDF
                                    </span>
                                </div>
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                                    Rapport catalogue imprimable avec fiches
                                </p>
                            </div>
                        </button>

                        {/* CSV Option */}
                        <button
                            type="button"
                            onClick={() => handleQuickExport('csv')}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors group"
                        >
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                                <TableCellsIcon className="w-4.5 h-4.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                                        Tableau CSV / Excel
                                    </span>
                                    <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 font-mono">
                                        .CSV
                                    </span>
                                </div>
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                                    Données tabulaires (UTF-8 BOM Excel)
                                </p>
                            </div>
                        </button>

                        {/* JSON Option */}
                        <button
                            type="button"
                            onClick={() => handleQuickExport('json')}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors group"
                        >
                            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                                <CodeBracketIcon className="w-4.5 h-4.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                        Données JSON Brutes
                                    </span>
                                    <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 font-mono">
                                        .JSON
                                    </span>
                                </div>
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                                    Format structuré pour sauvegarde / API
                                </p>
                            </div>
                        </button>
                    </div>

                    <div className="p-1.5 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-800">
                        <button
                            type="button"
                            onClick={() => { setIsOpen(false); setIsModalOpen(true) }}
                            className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                            <AdjustmentsHorizontalIcon className="w-3.5 h-3.5 text-orange-500" />
                            Options d&apos;exportation avancées…
                        </button>
                    </div>
                </div>
            )}

            {/* Advanced Export Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-xl w-full border border-slate-200 dark:border-slate-800 overflow-hidden animate-fade-in-down">
                        {/* Header */}
                        <div className="cargo-bg px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-950/60 border border-orange-200 dark:border-orange-800 flex items-center justify-center flex-shrink-0">
                                    <ArrowDownTrayIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                </div>
                                <div>
                                    <h3 className="font-extrabold text-slate-900 dark:text-white text-base uppercase tracking-tight">
                                        Exporter le catalogue produits
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                                        Sélectionnez le format et les critères d&apos;exportation
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* 1. Format Cards */}
                            <div>
                                <label className="block text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2.5">
                                    1. Choisir le format du fichier
                                </label>
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        {
                                            id: 'pdf' as const,
                                            title: 'PDF Document',
                                            ext: '.PDF',
                                            desc: 'Rapport imprimable',
                                            icon: DocumentTextIcon,
                                            color: 'border-red-500 text-red-600 bg-red-50/60 dark:bg-red-950/30'
                                        },
                                        {
                                            id: 'csv' as const,
                                            title: 'Tableau CSV',
                                            ext: '.CSV',
                                            desc: 'Compatible Excel',
                                            icon: TableCellsIcon,
                                            color: 'border-emerald-500 text-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/30'
                                        },
                                        {
                                            id: 'json' as const,
                                            title: 'Fichier JSON',
                                            ext: '.JSON',
                                            desc: 'Données brutes API',
                                            icon: CodeBracketIcon,
                                            color: 'border-blue-500 text-blue-600 bg-blue-50/60 dark:bg-blue-950/30'
                                        }
                                    ].map(fmt => {
                                        const Icon = fmt.icon
                                        const isSelected = selectedFormat === fmt.id
                                        return (
                                            <button
                                                key={fmt.id}
                                                type="button"
                                                onClick={() => setSelectedFormat(fmt.id)}
                                                className={`relative p-3.5 rounded-xl border-2 text-left transition-all flex flex-col justify-between ${isSelected
                                                        ? `${fmt.color} shadow-md`
                                                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                                                    }`}
                                            >
                                                {isSelected && (
                                                    <CheckCircleIcon className="absolute top-2.5 right-2.5 w-4 h-4 text-orange-500" />
                                                )}
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Icon className={`w-5 h-5 ${isSelected ? '' : 'text-slate-400'}`} />
                                                    <span className="text-xs font-mono font-black">{fmt.ext}</span>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold text-slate-900 dark:text-white">{fmt.title}</p>
                                                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{fmt.desc}</p>
                                                </div>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* 2. Scope Selection */}
                            <div>
                                <label className="block text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2.5">
                                    2. Périmètre des données
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className={`p-3 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${exportScope === 'filtered'
                                            ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-950/30 text-slate-900 dark:text-white'
                                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                        }`}>
                                        <input
                                            type="radio"
                                            name="exportScope"
                                            checked={exportScope === 'filtered'}
                                            onChange={() => setExportScope('filtered')}
                                            className="mt-0.5 text-orange-500 focus:ring-orange-500"
                                        />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold">Produits filtrés</span>
                                                <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300">
                                                    {products.length} réf.
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                                                Selon votre recherche et les filtres actifs
                                            </p>
                                        </div>
                                    </label>

                                    <label className={`p-3 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${exportScope === 'all'
                                            ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-950/30 text-slate-900 dark:text-white'
                                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                        }`}>
                                        <input
                                            type="radio"
                                            name="exportScope"
                                            checked={exportScope === 'all'}
                                            onChange={() => setExportScope('all')}
                                            className="mt-0.5 text-orange-500 focus:ring-orange-500"
                                        />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold">Catalogue complet</span>
                                                <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                                    {allProducts.length} réf.
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                                                Exporte l&apos;intégralité des produits enregistrés
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* 3. Live Preview Box */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        Aperçu des données ({productsToExport.length} référence(s))
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleCopyPreview}
                                        className="text-[11px] font-bold text-orange-600 dark:text-orange-400 hover:underline flex items-center gap-1"
                                    >
                                        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                                        {copied ? 'Copié !' : 'Copier l\'aperçu'}
                                    </button>
                                </div>

                                <div className="bg-slate-900 rounded-xl p-3 border border-slate-800 font-mono text-[11px] text-slate-300 max-h-36 overflow-auto">
                                    {selectedFormat === 'json' ? (
                                        <pre className="whitespace-pre-wrap">
                                            {JSON.stringify(productsToExport.slice(0, 3).map(p => ({
                                                id: p.id,
                                                name: p.name,
                                                sku: p.sku,
                                                dimensions: `${p.length || '?'}×${p.width || '?'}×${p.height || '?'} ${p.dimension_unit || 'cm'}`,
                                                weight: `${p.weight || '?'} ${p.weight_unit || 'kg'}`,
                                                is_dangerous_goods: p.is_dangerous_goods || false
                                            })), null, 2)}
                                        </pre>
                                    ) : selectedFormat === 'csv' ? (
                                        <pre className="whitespace-pre-wrap">
                                            {`ID;SKU;Nom;Longueur;Largeur;Hauteur;Poids;Statut\n` +
                                                productsToExport.slice(0, 4).map(p =>
                                                    `"${p.id || ''}";"${p.sku || ''}";"${p.name || ''}";${p.length || 0};${p.width || 0};${p.height || 0};${p.weight || 0};"${p.status || 'active'}"`
                                                ).join('\n')
                                            }
                                        </pre>
                                    ) : (
                                        <div className="space-y-1 text-slate-300">
                                            <p className="text-orange-400 font-bold">📄 RAPPORT PDF CATALOGUE PRODUITS TMS</p>
                                            <p>• Titre : Catalogue Produits TMS 3D Optimizer</p>
                                            <p>• Nombre de références : {productsToExport.length}</p>
                                            <p>• Tableau récapitulatif avec dimensions, poids, classe ADR et température</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Modal Actions Footer */}
                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                onClick={handleModalExport}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-md hover:-translate-y-0.5 transition-all"
                            >
                                <ArrowDownTrayIcon className="w-4 h-4" />
                                Télécharger le fichier ({selectedFormat.toUpperCase()})
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
